import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { Post } from "../components/Post";
import { TagsBlock } from "../components/TagsBlock";
import { CommentsBlock } from "../components/CommentsBlock";
import { fetchPosts, fetchPostsByTag } from "../redux/slices/posts";
import axios from "../axios";
import styles from "./Home.module.scss";

export const Home = () => {
  const { tag } = useParams();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.data);
  const { posts } = useSelector((state) => state.posts);
  const [activeTab, setActiveTab] = useState(0); // Состояние для активной вкладки
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 15;
  const [commentsCounts, setCommentsCounts] = useState({});
  const [latestComments, setLatestComments] = useState([]);
  const [isCommentsLoading, setIsCommentsLoading] = useState(true);
  const [isBannerClosed, setIsBannerClosed] = useState(
    localStorage.getItem("bannerClosed") === "true",
  );
  const [currentCommentsPage, setCurrentCommentsPage] = useState(1); // Добавлено состояние для пагинации комментариев
  const commentsPerPage = 20; // Количество комментариев на страницу

  const isPostsLoading = posts.status === "loading";

  // Эффект для закрытия баннера
  const handleCloseBanner = () => {
    setIsBannerClosed(true);
    localStorage.setItem("bannerClosed", "true");
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, tag]);

  // Прокрутка страницы вверх при изменении страницы
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  // Сортируем посты в зависимости от выбранной вкладки
  const sortedPosts = useMemo(() => {
    if (isPostsLoading || !posts.items) return [];
    return [...posts.items].sort((a, b) => {
      if (activeTab === 1) {
        // Сортировка по дате (новые сначала)
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else {
        // Сортировка по просмотрам (популярные сначала)
        return b.viewsCount - a.viewsCount;
      }
    });
  }, [posts.items, isPostsLoading, activeTab]); // Добавлена зависимость от activeTab

  // Посты для текущей страницы
  const currentPosts = useMemo(() => {
    const startIndex = (currentPage - 1) * postsPerPage;
    return sortedPosts.slice(startIndex, startIndex + postsPerPage);
  }, [sortedPosts, currentPage]);
  // Проверка доступности кнопок
  const totalPages = Math.ceil(sortedPosts.length / postsPerPage);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  //Получение тегов из всех постов
  const allTags = useMemo(() => {
    if (isPostsLoading || !posts.items) return [];
    const tags = posts.items.flatMap((post) => post.tags || []);
    // Подсчет частоты тегов
    const tagCount = tags.reduce((acc, tag) => {
      acc[tag] = (acc[tag] || 0) + 1;
      return acc;
    }, {});
    // Сортировка тегов по убыванию частоты
    return Object.keys(tagCount).sort((a, b) => tagCount[b] - tagCount[a]);
  }, [posts.items, isPostsLoading]);

  useEffect(() => {
    if (tag) {
      dispatch(fetchPostsByTag(tag));
    } else {
      dispatch(fetchPosts());
    }
  }, [tag, dispatch]);

  useEffect(() => {
    if (!isPostsLoading && posts.items) {
      posts.items.forEach((post) => {
        axios
          .get(`/comments/${post._id}`)
          .then((res) => {
            setCommentsCounts((prev) => ({
              ...prev,
              [post._id]: res.data.length,
            }));
          })
          .catch(console.warn);
      });
    }
  }, [isPostsLoading, posts.items]);

  // Эффект для загрузки последних комментариев
  useEffect(() => {
    axios
      .post("/comments/latest") // Предполагаемый эндпоинт для последних комментариев
      .then((res) => {
        setLatestComments(res.data);
        setIsCommentsLoading(false);
      })
      .catch((err) => {
        console.warn(err);
        setIsCommentsLoading(false);
      });
  }, []);

  // Вычисляем текущие комментарии для отображения
  const currentComments = useMemo(() => {
    const startIndex = (currentCommentsPage - 1) * commentsPerPage;
    return latestComments.slice(startIndex, startIndex + commentsPerPage);
  }, [latestComments, currentCommentsPage]);

  // Проверка доступности кнопок для комментариев
  const totalCommentsPages = Math.ceil(latestComments.length / commentsPerPage);
  const canGoPrevComments = currentCommentsPage > 1;
  const canGoNextComments = currentCommentsPage < totalCommentsPages;

  return (
    <>
      {/* Баннер для неавторизованных пользователей */}
      {!userData && !isBannerClosed && (
        <Dialog
          open={true}
          onClose={handleCloseBanner}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              height: "20vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            },
          }}>
          <IconButton
            aria-label="close"
            onClick={handleCloseBanner}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}>
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Typography variant="h6" textAlign="center">
              Щоб написати пост або додати коментар, необхідно зареєструватись.
            </Typography>
          </DialogContent>
        </Dialog>
      )}
      <div className={styles.container}>
        <div className={styles.homeTop}>
          <div className={styles.blank}>
            <h4>
              Увага! Пишіть коментарі, відгуки та загальні питання щодо даних
              виробів. На даному ресурсі <span>вітається</span> розміщення
              постів із зображеннями або відео роботи цього верстата, а також
              пряма реклама виробів, виконаних на даному верстаті (фото ножів,
              ціна, контактна інформація користувача).
            </h4>
          </div>
          <Tabs
            className={styles.tabs}
            style={{ marginBottom: 15 }}
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="basic tabs example">
            <Tab className={styles.tab} label="Популярні" />
            <Tab className={styles.tab} label="Нові" />
          </Tabs>
        </div>

        <Grid container spacing={4} className={styles.gridContainer}>
          <Grid xs={8} item className={styles.gridItemLeft}>
            {(isPostsLoading ? [...Array(5)] : currentPosts).map((obj, index) =>
              isPostsLoading ? (
                <Post key={index} isLoading={true} />
              ) : (
                <Post
                  key={obj._id}
                  slug={obj.slug}
                  id={obj._id}
                  title={obj.title}
                  imageUrl={obj.imageUrl}
                  videoUrl={
                    obj.videoUrl
                      ? obj.videoUrl.startsWith("http")
                        ? obj.videoUrl
                        : `process.env.REACT_APP_API_URL${obj.videoUrl}`
                      : ""
                  }
                  user={obj.user}
                  createdAt={obj.createdAt}
                  viewsCount={obj.viewsCount}
                  commentsCount={commentsCounts[obj._id] || 0}
                  tags={obj.tags}
                  isEditable={
                    userData?._id === obj.user._id || userData?.isAdmin
                  }
                />
              ),
            )}
          </Grid>
          <Grid xs={4} item className={styles.gridItemRight}>
            <TagsBlock items={allTags} isLoading={isPostsLoading} />
            <CommentsBlock
              items={currentComments.map((comment) => ({
                user: {
                  fullName: comment.user.fullName,
                  avatarUrl: comment.user.avatarUrl,
                },
                text: comment.text,
                postId: comment.post._id, // Добавляем ID поста в пропсы
                createdAt: comment.createdAt,
              }))}
              isLoading={isCommentsLoading}
            />
            {/* Пагинация для комментариев */}
            <div
              style={{
                marginTop: 20,
                display: "flex",
                justifyContent: "center",
                gap: 10,
              }}>
              <Button
                variant="contained"
                onClick={() => setCurrentCommentsPage((p) => p - 1)}
                disabled={!canGoPrevComments}>
                Назад
              </Button>
              <Button
                variant="contained"
                onClick={() => setCurrentCommentsPage((p) => p + 1)}
                disabled={!canGoNextComments}>
                Вперед
              </Button>
            </div>
          </Grid>
        </Grid>

        {/* Кнопки пагинации Постов */}
        <div
          className={styles.pagination}
          style={{
            marginTop: 20,
            marginBottom: 20,
            display: "flex",
            justifyContent: "center",
            gap: 10,
          }}>
          <Button
            variant="contained"
            onClick={() => setCurrentPage((p) => p - 1)}
            disabled={!canGoPrev}>
            Назад
          </Button>
          <Button
            variant="contained"
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={!canGoNext}>
            Вперед
          </Button>
        </div>
      </div>
    </>
  );
};
